import PageHeader from '../../components/layout/PageHeader.jsx';
import { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Auth } from '../../StateManagment/Auth.js';
import Location from "../../hooks/Location.js"
import { CalendarView } from '../../components/calendar/CalendarView.jsx';
import { useFirestoreGeneral, useFirestoreNoCompagny } from '../../firebase/useFirestore.js';
import CalendarFilters from '../../components/calendar/CalendarFilters.jsx';
import resolveAgendaView from '../../helpers/calendar/resolveAgendaView.js';
import useAgendaData from '../../hooks/calendar/useAgendaData.js';
import useSettings from '../../hooks/Settings.jsx';
import useSharedCollection from "../../firebase/useSharedCollection";
import useAdressStatusById from "../../hooks/useAdressStatusById";
import { buildLocationByAdressId } from "../../helpers/locations/locationFilter";
import hasLocationFilter from "../../helpers/dashboardHelpers/hasLocationFilter";
import { householdAgendaPath, householdPlanningEnabled } from '../../helpers/board/resolveBoardView';
import { client } from '../../hooks/Client';

const Agenda = () => {
  const [auth] = useContext(Auth);
  const settings = useSettings();
  const history = useHistory();

  // Locaties zijn één instelling, gedeeld met de deelnemers. Een huisbezoek
  // draagt zelf geen locatie (gemeten: 0 van de 580) en erft hem van het
  // huishouden, dus die koppeling hoort hier gelegd te worden.
  // AI-CONTEXT: See docs/features/huishouden-locaties.md
  const locations = useSharedCollection('Locations');
  // Alleen ophalen als er iets te filteren valt. Zonder deze poort betaalt een
  // organisatie met nul of één locatie bij elk bezoek aan de agenda de lezing
  // van al haar huishoudens (~394 bij de grootste) voor een keuzelijst die ze
  // nooit te zien krijgt.
  // Met de module Huishoudens komen ze óók binnen: het agendablok van een
  // huisbezoek toont dan de status van het huishouden als bolletje met tekst
  // (verzoek BuurtKlusBedrijf). Zonder die module is er geen huisbezoek te
  // tekenen, dus ook niets te lezen.
  const adresses = useSharedCollection(
      (hasLocationFilter(locations) || householdPlanningEnabled(settings)) ? 'Adresses' : null,
  );
  const locationByAdressId = useMemo(
      () => buildLocationByAdressId(adresses),
      [adresses],
  );
  const adressStatusById = useAdressStatusById(adresses, settings.adressStatusConfig);

  // Dezelfde hook als de weergave 'Lijst en agenda' bij Huishoudens, zodat de
  // twee schermen niet uit elkaar lopen.
  const {
    visibleDocs,
    personas,
    setDateRange,
    refresh,
    personaAbsences,
    roleColorByPersonaId,
    personaColors,
    scheduleEndByPersonaId,
  } = useAgendaData();

  const urlId = Location()[3];
  const urlType = Location()[4];

  const AGENDA_CONTEXT_CONFIG = {
    measureMoment:   { collection: 'MeasureMoments', field: 'ID', titleField: 'Title' },
    activityForm:    { collection: 'ActivityForms',  field: 'ID', titleField: 'Title' },
    personaSchedule: { collection: 'Personas',        field: 'ID', titleField: 'Title' },
    addressVisit:    { collection: 'Adresses',        field: 'ID', titleField: 'Title' },
    teamSchedule:    { collection: 'Users',           field: 'ID', titleField: 'Title' },
  };

  const contextConfig = useMemo(() => AGENDA_CONTEXT_CONFIG[urlType] || null, [urlType]);

  const contextDocs = useFirestoreGeneral(
    contextConfig?.collection || '__NO_COLLECTION__',
    contextConfig?.field || 'ID',
    urlId || ''
  );

  // Een projectafspraak wijst naar een ANDERE omgeving. De hook hierboven plakt
  // `CompagnyID == client` over elke query, en dat sluit een vreemde tenant per
  // definitie uit — vandaar een aparte, ongescoopte lookup. CompagnyMeta is
  // openbaar leesbaar. AI-CONTEXT: See docs/features/deccos-projectafspraken.md
  const projectMeta = useFirestoreNoCompagny(
    'CompagnyMeta',
    'CompagnyID',
    urlType === 'projectFollowUp' ? (urlId || '') : ''
  );

  const contextTitle = useMemo(() => {
    if (urlType === 'projectFollowUp') {
      return Array.isArray(projectMeta) && projectMeta.length > 0
        ? (projectMeta[0].CommunityName || null)
        : null;
    }

    if (!contextConfig) return null;
    if (!contextDocs || contextDocs.length === 0) return null;
    return contextDocs[0][contextConfig.titleField || 'Title'] || null;
  }, [contextDocs, contextConfig, urlType, projectMeta]);

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={'Agenda'}
          subHeading={null}
          description={null}
          AI={false}
        />
        <div className="dashboard-container">
          {/* FullCalendar leest initialView alleen bij mount — wacht dus tot de
              instelling geladen is voordat de kalender rendert. */}
          {settings.loaded &&
          <CalendarFilters
            docs={visibleDocs}
            locations={locations}
            locationByAdressId={locationByAdressId}
            contextType={urlType || null}
            contextId={urlId || null}
            currentUserId={auth?.ID}
            // onFiltersChange={(filters) => console.log(filters)} // optional
          >
            {({ filteredDocs, setSelectedTypes, selectedTypes, selectedPersonaIds }) => (
              <CalendarView
                docs={filteredDocs}
                // Met precies één medewerker kan het rooster in week en dag een achtergrond zijn.
                selectedPersonaIds={selectedPersonaIds}
                scheduleDocs={visibleDocs}
                userId={auth?.ID}
                // De knop 'huishoudens' rechts in de knoppenbalk, naast het
                // rooster: de ingang naar 'Huishoudens inplannen' (verzoek
                // BuurtKlusBedrijf). Alleen met de module Huishoudens ('FIXtool').
                onOpenHouseholdPlanning={householdPlanningEnabled(settings)
                  ? () => history.push(householdAgendaPath(client))
                  : undefined}
                initialview={resolveAgendaView(settings.defaultAgendaView)}
                onDateRangeChange={setDateRange}
                onStaleData={refresh}
                contextTitle={contextTitle}
                contextId={urlId}
                contextType={urlType}
                onApplyTypeFilter={setSelectedTypes}
                selectedTypes={selectedTypes}
                roleColorByPersonaId={roleColorByPersonaId}
                personaColorById={personaColors}
                scheduleEndByPersonaId={scheduleEndByPersonaId}
                personaAbsences={personaAbsences}
                timelinePersonas={personas}
                adressStatusById={adressStatusById}
                showSearch
              />
            )}
          </CalendarFilters>
          }
        </div>
      </div>
    </div>
  );
};

export default Agenda;
